import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Progress } from '../ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import {
  Mail,
  Plus,
  Truck,
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle,
  ArrowRight,
  Calendar,
  Hash,
  ChevronDown,
  ChevronUp,
  Loader2,
  Trash2,
  Save,
  Lock,
} from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_CONFIG = {
  pending: { color: 'bg-gray-100 text-gray-700', icon: Clock, label: 'Not Yet Mailed' },
  mailed: { color: 'bg-blue-100 text-blue-700', icon: Mail, label: 'Mailed' },
  in_transit: { color: 'bg-indigo-100 text-indigo-700', icon: Truck, label: 'In Transit' },
  delivered: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Delivered' },
  waiting_for_response: { color: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Awaiting Response' },
  expired: { color: 'bg-red-100 text-red-700', icon: AlertTriangle, label: 'Window Expired' },
  response_received: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle, label: 'Response Received' },
  exception: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Delivery Issue' },
  delivery_exception: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Delivery Issue' },
};

const COUNTDOWN_COLORS = {
  pending: '[&>div]:bg-gray-400',
  in_transit: '[&>div]:bg-blue-500',
  waiting_for_response: '[&>div]:bg-amber-500',
  expired: '[&>div]:bg-red-500',
  response_received: '[&>div]:bg-emerald-500',
  delivery_exception: '[&>div]:bg-red-600',
};

const MailingCard = ({ mailing, onUpdate, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({
    tracking_number: mailing.tracking_number || '',
    mailed_at: mailing.mailed_at ? mailing.mailed_at.split('T')[0] : '',
    estimated_delivery: mailing.estimated_delivery ? mailing.estimated_delivery.split('T')[0] : '',
    actual_delivery: mailing.actual_delivery ? mailing.actual_delivery.split('T')[0] : '',
    response_received_at: mailing.response_received_at ? mailing.response_received_at.split('T')[0] : '',
    delivery_exception: mailing.delivery_exception || '',
  });
  const [saving, setSaving] = useState(false);

  const cd = mailing.countdown || {};
  const rec = mailing.recommendation || {};
  const overallStatus = cd.countdown_status || mailing.delivery_status || 'pending';
  const statusCfg = STATUS_CONFIG[overallStatus] || STATUS_CONFIG.pending;
  const StatusIcon = statusCfg.icon;

  const handleSave = async () => {
    setSaving(true);
    const updates = {};
    if (form.tracking_number !== mailing.tracking_number) updates.tracking_number = form.tracking_number;
    if (form.mailed_at && form.mailed_at !== (mailing.mailed_at || '').split('T')[0]) updates.mailed_at = form.mailed_at + 'T00:00:00Z';
    if (form.estimated_delivery) updates.estimated_delivery = form.estimated_delivery + 'T00:00:00Z';
    if (form.actual_delivery) updates.actual_delivery = form.actual_delivery + 'T00:00:00Z';
    if (form.response_received_at) updates.response_received_at = form.response_received_at + 'T00:00:00Z';
    if (form.delivery_exception) updates.delivery_exception = form.delivery_exception;
    await onUpdate(mailing.mailing_id, updates);
    setEditing(false);
    setSaving(false);
  };

  return (
    <Card className="border" data-testid={`mailing-card-${mailing.mailing_id}`}>
      <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-blue-600" />
            <div>
              <CardTitle className="text-sm">{mailing.document_name}</CardTitle>
              <p className="text-xs text-gray-500">{mailing.remedy_label} &mdash; To: {mailing.recipient_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`text-xs ${statusCfg.color}`}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {statusCfg.label}
            </Badge>
            {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
        </div>

        {cd.countdown_status === 'waiting_for_response' && (
          <div className="mt-3 space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-xs text-amber-700 font-medium">{cd.display}</span>
              <span className="text-xs text-gray-500">Due: {cd.response_due_at ? new Date(cd.response_due_at).toLocaleDateString() : '--'}</span>
            </div>
            <Progress value={cd.progress_pct || 0} className={`h-2 ${COUNTDOWN_COLORS.waiting_for_response}`} />
          </div>
        )}
        {cd.countdown_status === 'expired' && (
          <div className="mt-2 flex items-center gap-2 p-2 bg-red-50 rounded-lg border border-red-200">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <span className="text-xs text-red-800 font-medium">{cd.display}</span>
          </div>
        )}
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="space-y-1">
              <p className="text-gray-500">Tracking Number</p>
              {editing ? (
                <Input size="sm" value={form.tracking_number} onChange={e => setForm(f => ({...f, tracking_number: e.target.value}))} className="h-8 text-xs" placeholder="Enter tracking #" />
              ) : (
                <p className="font-mono font-medium text-gray-900">{mailing.tracking_number || '—'}</p>
              )}
            </div>
            <div className="space-y-1">
              <p className="text-gray-500">Mail Class</p>
              <p className="font-medium">{mailing.mail_class}</p>
            </div>
            <div className="space-y-1">
              <p className="text-gray-500">Mailed Date</p>
              {editing ? (
                <Input type="date" value={form.mailed_at} onChange={e => setForm(f => ({...f, mailed_at: e.target.value}))} className="h-8 text-xs" />
              ) : (
                <p className="font-medium">{mailing.mailed_at ? new Date(mailing.mailed_at).toLocaleDateString() : '—'}</p>
              )}
            </div>
            <div className="space-y-1">
              <p className="text-gray-500">Actual Delivery</p>
              {editing ? (
                <Input type="date" value={form.actual_delivery} onChange={e => setForm(f => ({...f, actual_delivery: e.target.value}))} className="h-8 text-xs" />
              ) : (
                <p className="font-medium">{mailing.actual_delivery ? new Date(mailing.actual_delivery).toLocaleDateString() : '—'}</p>
              )}
            </div>
            <div className="space-y-1">
              <p className="text-gray-500">Response Window</p>
              <p className="font-medium">{mailing.response_window_days} days ({mailing.response_window_rule?.replace(/_/g, ' ')})</p>
            </div>
            <div className="space-y-1">
              <p className="text-gray-500">Response Due</p>
              <p className="font-medium">{cd.response_due_at ? new Date(cd.response_due_at).toLocaleDateString() : '—'}</p>
            </div>
          </div>

          {editing && (
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="space-y-1">
                <p className="text-gray-500">Response Received Date</p>
                <Input type="date" value={form.response_received_at} onChange={e => setForm(f => ({...f, response_received_at: e.target.value}))} className="h-8 text-xs" />
              </div>
              <div className="space-y-1">
                <p className="text-gray-500">Delivery Exception</p>
                <Input value={form.delivery_exception} onChange={e => setForm(f => ({...f, delivery_exception: e.target.value}))} className="h-8 text-xs" placeholder="e.g. Returned to sender" />
              </div>
            </div>
          )}

          {rec.next_label && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-1">
                <ArrowRight className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-semibold text-blue-800">Next Recommended Step</span>
              </div>
              <p className="text-sm font-medium text-blue-900">{rec.next_label}</p>
              {rec.earliest_send_date && (
                <p className="text-xs text-blue-600 mt-1">Earliest send date: {rec.earliest_send_date}</p>
              )}
              <p className="text-xs text-gray-600 mt-1">{rec.reason}</p>
            </div>
          )}

          <div className="flex items-center gap-2 pt-2 border-t">
            {editing ? (
              <>
                <Button size="sm" onClick={handleSave} disabled={saving} className="text-xs">
                  {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                  Save
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="text-xs">Cancel</Button>
              </>
            ) : (
              <>
                <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="text-xs" data-testid={`edit-mailing-${mailing.mailing_id}`}>
                  Update Tracking
                </Button>
                <Button size="sm" variant="outline" onClick={() => onDelete(mailing.mailing_id)} className="text-xs text-red-600 hover:text-red-700">
                  <Trash2 className="w-3 h-3 mr-1" /> Remove
                </Button>
              </>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

const AddMailingForm = ({ conversationId, chains, mailClasses, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    document_name: '',
    remedy_step: 'conditional_acceptance',
    recipient_name: '',
    recipient_address: '',
    mail_class: 'Certified Mail - Return Receipt',
    tracking_number: '',
    mailed_at: new Date().toISOString().split('T')[0],
    response_window_days: '',
    response_window_rule: '',
  });

  const handleCreate = async () => {
    if (!form.document_name || !form.recipient_name) return;
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        conversation_id: conversationId,
        document_name: form.document_name,
        remedy_step: form.remedy_step,
        recipient_name: form.recipient_name,
        recipient_address: form.recipient_address,
        mail_class: form.mail_class,
        tracking_number: form.tracking_number,
        mailed_at: form.mailed_at ? form.mailed_at + 'T00:00:00Z' : null,
      };
      if (form.response_window_days) payload.response_window_days = parseInt(form.response_window_days);
      if (form.response_window_rule) payload.response_window_rule = form.response_window_rule;

      await axios.post(`${API_BASE}/case-mailings/`, payload, { headers: { Authorization: `Bearer ${token}` } });
      onCreated();
      setOpen(false);
      setForm(f => ({...f, document_name: '', recipient_name: '', tracking_number: '', recipient_address: ''}));
    } catch (err) {
      console.error('Create mailing failed:', err);
    } finally {
      setCreating(false);
    }
  };

  if (!open) {
    return (
      <Button onClick={() => setOpen(true)} className="w-full" variant="outline" data-testid="add-mailing-btn">
        <Plus className="w-4 h-4 mr-2" /> Add Mailing Record
      </Button>
    );
  }

  return (
    <Card className="border-2 border-blue-200 bg-blue-50/30">
      <CardContent className="p-4 space-y-3">
        <p className="text-sm font-semibold text-gray-900">New Mailing Record</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs text-gray-600">Document Name</label>
            <Input value={form.document_name} onChange={e => setForm(f => ({...f, document_name: e.target.value}))} placeholder="e.g. Conditional Acceptance" className="h-8 text-xs" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-gray-600">Remedy Step</label>
            <Select value={form.remedy_step} onValueChange={v => setForm(f => ({...f, remedy_step: v}))}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(chains).map(([k, v]) => (
                  <SelectItem key={k} value={k} className="text-xs">{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-gray-600">Recipient Name</label>
            <Input value={form.recipient_name} onChange={e => setForm(f => ({...f, recipient_name: e.target.value}))} placeholder="e.g. State of Tennessee" className="h-8 text-xs" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-gray-600">Recipient Address</label>
            <Input value={form.recipient_address} onChange={e => setForm(f => ({...f, recipient_address: e.target.value}))} placeholder="Full address" className="h-8 text-xs" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-gray-600">Mail Class</label>
            <Select value={form.mail_class} onValueChange={v => setForm(f => ({...f, mail_class: v}))}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(mailClasses || []).map(c => (
                  <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-gray-600">Tracking Number</label>
            <Input value={form.tracking_number} onChange={e => setForm(f => ({...f, tracking_number: e.target.value}))} placeholder="USPS tracking #" className="h-8 text-xs" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-gray-600">Mailed Date</label>
            <Input type="date" value={form.mailed_at} onChange={e => setForm(f => ({...f, mailed_at: e.target.value}))} className="h-8 text-xs" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-gray-600">Response Window (days, blank for default)</label>
            <Input type="number" value={form.response_window_days} onChange={e => setForm(f => ({...f, response_window_days: e.target.value}))} placeholder="Default from template" className="h-8 text-xs" />
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={handleCreate} disabled={creating || !form.document_name || !form.recipient_name} className="text-xs">
            {creating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
            Create Mailing
          </Button>
          <Button size="sm" variant="outline" onClick={() => setOpen(false)} className="text-xs">Cancel</Button>
        </div>
      </CardContent>
    </Card>
  );
};

const MailingRoom = ({ conversationId, isPaid }) => {
  const [mailings, setMailings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chains, setChains] = useState({});
  const [mailClasses, setMailClasses] = useState([]);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchMailings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/case-mailings/${conversationId}`, { headers });
      setMailings(res.data.mailings || []);
    } catch (err) {
      console.error('Failed to fetch mailings:', err);
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/case-mailings/templates/remedy-chains`, { headers });
      setChains(res.data.chains || {});
      setMailClasses(res.data.mail_classes || []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  }, []);

  useEffect(() => {
    if (conversationId && isPaid) {
      fetchMailings();
      fetchTemplates();
    } else {
      setLoading(false);
    }
  }, [conversationId, isPaid]);

  const handleUpdate = async (mailingId, updates) => {
    try {
      await axios.put(`${API_BASE}/case-mailings/${mailingId}`, updates, { headers });
      fetchMailings();
    } catch (err) {
      console.error('Update failed:', err);
    }
  };

  const handleDelete = async (mailingId) => {
    try {
      await axios.delete(`${API_BASE}/case-mailings/${mailingId}`, { headers });
      fetchMailings();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  if (!isPaid) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 max-w-lg" data-testid="mailing-upgrade-gate">
          <CardContent className="p-10 text-center space-y-4">
            <Lock className="w-12 h-12 mx-auto text-amber-600" />
            <h2 className="text-xl font-bold text-gray-900">Paid Feature</h2>
            <p className="text-sm text-gray-700">The Mailing Room is available exclusively for paid subscribers.</p>
            <Button className="bg-gradient-to-r from-amber-600 to-orange-600 text-white px-8" onClick={() => { window.location.href = '/dashboard/subscription'; }}>
              Upgrade Plan
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const activeMailings = mailings.filter(m => m.countdown?.countdown_status !== 'response_received');
  const completedMailings = mailings.filter(m => m.countdown?.countdown_status === 'response_received');
  const expiredCount = mailings.filter(m => m.countdown?.countdown_status === 'expired').length;

  return (
    <ScrollArea className="h-[calc(100vh-200px)]">
      <div className="p-4 space-y-4 max-w-4xl mx-auto" data-testid="mailing-room">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="w-6 h-6 text-blue-700" />
            <h2 className="text-lg font-bold text-gray-900">Mailing Room</h2>
            <Badge variant="secondary" className="text-xs">{mailings.length} mailing{mailings.length !== 1 ? 's' : ''}</Badge>
          </div>
          {expiredCount > 0 && (
            <Badge className="bg-red-100 text-red-700 text-xs">
              <AlertTriangle className="w-3 h-3 mr-1" />
              {expiredCount} expired
            </Badge>
          )}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="mailing-room-description">
          <p className="text-sm text-gray-700 leading-relaxed">
            The <strong>Mailing Room</strong> is your personal certified mail command center. Every document you send in your administrative process — from your First Mailing through to Administrative Closure — needs to be tracked with precision. Here you can log each mailing with its <strong>USPS certified tracking number</strong>, monitor delivery status, and watch <strong>response window countdowns</strong> in real time. When a deadline expires without a response, the Mailing Room flags it and recommends your next step in the remedy chain. This ensures your entire paper trail is documented, timestamped, and ready to demonstrate that you acted in honor throughout the process.
          </p>
        </div>

        {mailings.length > 0 && (
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: 'Total', value: mailings.length, color: 'text-blue-700' },
              { label: 'Awaiting', value: mailings.filter(m => m.countdown?.countdown_status === 'waiting_for_response').length, color: 'text-amber-700' },
              { label: 'Expired', value: expiredCount, color: 'text-red-700' },
              { label: 'Received', value: completedMailings.length, color: 'text-emerald-700' },
            ].map(s => (
              <Card key={s.label}>
                <CardContent className="p-3 text-center">
                  <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-gray-500">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <AddMailingForm conversationId={conversationId} chains={chains} mailClasses={mailClasses} onCreated={fetchMailings} />

        {activeMailings.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm font-semibold text-gray-700">Active Mailings</p>
            {activeMailings.map(m => (
              <MailingCard key={m.mailing_id} mailing={m} onUpdate={handleUpdate} onDelete={handleDelete} />
            ))}
          </div>
        )}

        {completedMailings.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm font-semibold text-gray-500">Completed</p>
            {completedMailings.map(m => (
              <MailingCard key={m.mailing_id} mailing={m} onUpdate={handleUpdate} onDelete={handleDelete} />
            ))}
          </div>
        )}

        {mailings.length === 0 && (
          <div className="text-center py-12 space-y-3">
            <Mail className="w-12 h-12 mx-auto text-gray-300" />
            <p className="text-sm text-gray-500">No mailings tracked for this case yet.</p>
            <p className="text-xs text-gray-400">Add a mailing record after sending a remedy document to start tracking deadlines.</p>
          </div>
        )}
      </div>
    </ScrollArea>
  );
};

export default MailingRoom;
